import {
  AuthorizationType,
  BankAccountAccessRights,
  CreateApplicationRequest,
  CreateBankAccountParams,
  DigiLaErrorCode,
  DigiLaFlow,
  DigiLaMinorApplication,
  MinorServiceType,
} from '@shared/digila'
import { assertValue } from '@shared/helpers'
import { AuthenticationMode, DigiLaSource, DigiliFlow, DigiLiSource } from '@shared/types'
import { CommonAnalyticsModalName } from '@ui-common/analytics'
import { TransitionErrorIndicatorProps } from '@ui-common/components/TransitionErrorIndicator'
import { InputParams } from '@ui-common/steps/InitializeStep/initializeStepMachine'
import { CommonQueryParam, YesNo } from '@ui-common/types'
import {
  getSourceChannel,
  resolveAndStoreParam,
  resolveBooleanInputToYesNo,
  resolveYesNoToBooleanInput,
  toNumber,
} from '@ui-common/utils/helpers'
import { getLanguage, resolveAndStoreLanguage } from '@ui-common/utils/language'
import { ErrorVisualMode } from '@ui-components/containers/ErrorComponentTypes'
import { IntlShape } from 'react-intl'
import { clone } from 'remeda'

import { DigilaModalEventName } from '../analytics/analytics'
import { BankAccountDetailsFormData } from '../components/ServiceWizard/BankAccountService/BankAccountDetailsStep/types'
import { MinorUser, Phases, WizardData } from '../types'

export const getAndStoreInputParams = (): InputParams => {
  const sourceChannel = resolveAndStoreParam(
    CommonQueryParam.SourceChannel,
    Object.values(DigiLaSource),
    DigiLaSource.Unknown,
  )
  const flow = resolveAndStoreParam(CommonQueryParam.Flow, Object.values(DigiLaFlow), DigiLaFlow.Default)
  resolveAndStoreLanguage()
  return { flow, sourceChannel }
}

export const getPhases = (
  minorUser: MinorUser,
  selectedServices: MinorServiceType[],
  selectedAuthorizations: AuthorizationType[],
): Phases => {
  const phases: Phases = {
    minorKyc: {
      shouldBeUpdated: minorUser.isKycRequired,
      isComplete: false,
    },
    aoMembership: {
      shouldBeUpdated: !minorUser.isHouseholdMember,
      isComplete: false,
    },
    applyAuthorization:
      selectedAuthorizations?.length > 0
        ? {
            authorizations: selectedAuthorizations,
            isComplete: false,
          }
        : undefined,
    services: selectedServices.map((serviceType) => {
      return { type: serviceType, isComplete: false }
    }),
  }

  return phases
}

const isDigilaSourceChannel = (value: string | null): value is DigiLaSource => {
  return Object.values(DigiLaSource).includes(value as DigiLaSource)
}

export const getDigilaSourceChannel = (): DigiLaSource => {
  const sourceChannel = getSourceChannel()
  return isDigilaSourceChannel(sourceChannel) ? sourceChannel : DigiLaSource.Unknown
}

export const convertWizardDataToRequest = (wizardData: WizardData): CreateApplicationRequest => {
  if (
    (!wizardData.selectedServices || wizardData.selectedServices.length === 0) &&
    (!wizardData.selectedAuthorizations || wizardData.selectedAuthorizations?.length === 0)
  ) {
    throw new Error('Unexpected nothing selected')
  }

  const isApplyingBankAccount = isApplyingForService(wizardData, MinorServiceType.OpenBankAccount)
  const isApplyingInvestmentServices = isApplyingForService(wizardData, MinorServiceType.OpenInvestmentService)

  const request: CreateApplicationRequest = {}
  if (isApplyingBankAccount) {
    parseBankAccountData(request, wizardData)
  }

  if (isApplyingInvestmentServices) {
    parseInvestmentServiceData(request, wizardData)
  }

  request.secondGuardianPhoneNumber = wizardData.secondGuardianPhoneNumber

  if (wizardData.selectedAuthorizations && wizardData.selectedAuthorizations?.length > 0) {
    request.authorizations = {
      card: wizardData.selectedAuthorizations?.includes(AuthorizationType.PaymentCard),
      netbank: wizardData.selectedAuthorizations?.includes(AuthorizationType.NetbankCredentials),
    }
  }

  return request
}

export const convertApplicationToWizardData = (application: DigiLaMinorApplication | null): WizardData => {
  if (!application) {
    return {}
  }
  if (
    !application.createBankAccountParams &&
    !application.createInvestmentPortfolioParams &&
    !application.authorizations
  ) {
    throw new Error('Unexpected empty application')
  }

  const wizardData: WizardData = {
    selectedServices: [],
    selectedAuthorizations: [],
  }

  if (application.createBankAccountParams) {
    wizardData.selectedServices?.push(MinorServiceType.OpenBankAccount)
    reverseParseBankAccountData(application.createBankAccountParams, wizardData)
  }

  if (application.createInvestmentPortfolioParams) {
    wizardData.selectedServices?.push(MinorServiceType.OpenInvestmentService)
    const data = application.createInvestmentPortfolioParams
    wizardData.investmentServiceData = data.shouldUseCreatedBankAccount
      ? {
          useCreatedAccount: YesNo.Yes,
          bankAccount: undefined,
        }
      : {
          useCreatedAccount: YesNo.No,
          bankAccount: assertValue(data.bankAccountIban, 'data.bankAccountIban'),
        }
  }

  if (application.authorizations) {
    if (application.authorizations.card) {
      wizardData.selectedAuthorizations?.push(AuthorizationType.PaymentCard)
    }

    if (application.authorizations.netbank) {
      wizardData.selectedAuthorizations?.push(AuthorizationType.NetbankCredentials)
    }

    wizardData.isAuthorizationComplete = true
  }

  return wizardData
}

const isApplyingForService = (wizardData: WizardData, serviceType: MinorServiceType) => {
  return (wizardData.selectedServices ?? []).some((service) => service === serviceType)
}

const parseBankAccountData = (request: CreateApplicationRequest, wizardData: WizardData) => {
  const bankAccountData = assertValue(wizardData.bankAccountData, 'wizardData.bankAccountData')
  const bankAccountDetails = assertValue(bankAccountData.bankAccountDetails, 'bankAccountData.bankAccountDetails')

  const {
    childPermittedForAccount,
    attachAccountToMinorNetbank,
    guardianPaymentLimit,
    minorPaymentLimit,
    statementDeliveryInterval,
    statementDeliveryMethod,
    guardianAccessRights,
  } = bankAccountDetails

  request.createBankAccountParams = {
    bankAccountType: bankAccountData.bankAccountTypeData.bankAccountType,
    bankAccountDetails: {
      shouldAddRightsForMinor: assertValue(
        resolveYesNoToBooleanInput(childPermittedForAccount),
        'childPermittedForAccount',
      ),
      shouldAttachToMinorsNetbank: resolveYesNoToBooleanInput(attachAccountToMinorNetbank) ?? false,
      paymentLimitFirstGuardian: toNumber(guardianPaymentLimit),
      paymentLimitMinor: toNumber(minorPaymentLimit),
      statementDeliveryInterval,
      statementDeliveryMethod,
      guardianAccessRights: guardianAccessRights ?? BankAccountAccessRights.Wide,
    },
  }
}

const reverseParseBankAccountData = (createBankAccountParams: CreateBankAccountParams, wizardData: WizardData) => {
  const bankAccountType = assertValue(
    createBankAccountParams.bankAccountType,
    'createBankAccountParams.bankAccountType,',
  )
  const bankAccountDetails = assertValue(
    createBankAccountParams.bankAccountDetails,
    'createBankAccountParams.bankAccountDetails',
  )

  wizardData.bankAccountData = {
    bankAccountTypeData: { bankAccountType },
    bankAccountDetails: {
      attachAccountToMinorNetbank: resolveBooleanInputToYesNo(bankAccountDetails.shouldAttachToMinorsNetbank),
      childPermittedForAccount: bankAccountDetails.shouldAddRightsForMinor ? YesNo.Yes : YesNo.No,
      statementDeliveryMethod: bankAccountDetails.statementDeliveryMethod,
      statementDeliveryInterval: bankAccountDetails.statementDeliveryInterval,
      guardianAccessRights: bankAccountDetails.guardianAccessRights,
    } satisfies BankAccountDetailsFormData,
  }
}

const parseInvestmentServiceData = (request: CreateApplicationRequest, wizardData: WizardData) => {
  const data = assertValue(wizardData.investmentServiceData, 'wizardData.investmentServiceData')

  const useCreatedAccount = data.useCreatedAccount === YesNo.Yes

  request.createInvestmentPortfolioParams = {
    shouldUseCreatedBankAccount: useCreatedAccount,
    bankAccountIban: useCreatedAccount ? null : data.bankAccount.replace(/\s+/g, ''),
  }
}

export const getModalEventNameByErrorCode = (
  errorCode: DigiLaErrorCode,
): DigilaModalEventName | CommonAnalyticsModalName => {
  switch (errorCode) {
    case DigiLaErrorCode.UserIsNotMainAoMember:
      return DigilaModalEventName.UserIsNotMainAoMemberErrorModal
    case DigiLaErrorCode.RepresentativeRightsUnknown:
      return DigilaModalEventName.RepresentativeRightsUnknownErrorModal
    case DigiLaErrorCode.GuardianMismatch:
      return DigilaModalEventName.GuardianMismatchErrorModal
    case DigiLaErrorCode.OtherNonTechnical:
      return DigilaModalEventName.NonTechnicalErrorModal
    default:
      return 'ErrorModal'
  }
}

const completionCheck: Record<MinorServiceType, (data: WizardData) => boolean> = {
  [MinorServiceType.OpenBankAccount](data: WizardData): boolean {
    return (
      !!data.bankAccountData && data.bankAccountData.bankAccountTypeData && !!data.bankAccountData.bankAccountDetails
    )
  },
  [MinorServiceType.OpenInvestmentService](data: WizardData): boolean {
    return !!data.investmentServiceData
  },
}

export const getPhaseCompletion = (
  data: WizardData,
  phases: Phases | undefined,
  hasDataBeenCompleted: boolean,
): Phases | undefined => {
  if (!phases) {
    return undefined
  }

  const services = clone(phases).services
  const bankAccountPhase = services.find((phase) => phase.type === MinorServiceType.OpenBankAccount)
  const investmentServicePhase = services.find((phase) => phase.type === MinorServiceType.OpenInvestmentService)

  // Do not modify already completed
  if (hasDataBeenCompleted) {
    return {
      minorKyc: { ...phases.minorKyc },
      aoMembership: { ...phases.aoMembership },
      services,
      applyAuthorization: phases.applyAuthorization,
    }
  }

  if (bankAccountPhase) {
    bankAccountPhase.isComplete = completionCheck[MinorServiceType.OpenBankAccount](data)
  }

  if (investmentServicePhase) {
    investmentServicePhase.isComplete = completionCheck[MinorServiceType.OpenInvestmentService](data)
  }

  if (phases.applyAuthorization) {
    phases.applyAuthorization.isComplete = !!data.isAuthorizationComplete
  }

  return {
    ...phases,
    minorKyc: { ...phases.minorKyc },
    aoMembership: { ...phases.aoMembership },
    services,
  }
}

export const getUserInfoErrorProperties =
  (intl: IntlShape): ((errorCode?: DigiLaErrorCode, requestId?: string) => TransitionErrorIndicatorProps | undefined) =>
  (errorCode, requestId) => {
    if (errorCode === DigiLaErrorCode.OtherNonTechnical) {
      return {
        heading: intl.formatMessage({ id: 'common-non-technical-error-heading' }),
        text: intl.formatMessage({ id: 'digila-minor-services-unavailable-make-appointment-instead' }),
        mode: ErrorVisualMode.Plain,
        requestId,
      }
    }
  }

export const getAuthenticationMode = (sourceChannel: DigiLaSource): AuthenticationMode => {
  return sourceChannel === DigiLaSource.SMobile ? AuthenticationMode.Sso : AuthenticationMode.Interactive
}

export const getDigiliUrl = (baseUrl: string, sourceChannel: DigiLaSource, flow: DigiliFlow): string => {
  const digiliSources: Record<DigiLaSource, DigiLiSource> = {
    [DigiLaSource.Netbank]: DigiLiSource.DigiLaSpankkifi,
    [DigiLaSource.SPankkiFi]: DigiLiSource.DigiLaSpankkifi,
    [DigiLaSource.SMobile]: DigiLiSource.DigiLaSmobile,
    [DigiLaSource.DigiLiSMobile]: DigiLiSource.DigiLaSmobile,
    [DigiLaSource.Unknown]: DigiLiSource.DigiLaSpankkifi,
  }
  return `${baseUrl}?${CommonQueryParam.Flow}=${flow}&${CommonQueryParam.SourceChannel}=${digiliSources[sourceChannel]}&${CommonQueryParam.InitialLanguage}=${getLanguage()}`
}
